import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, ArrowLeft, Check, Instagram, Camera, MessageCircle,
  Calendar, Hash, Sparkles, BarChart3, Heart, Eye, Users, Zap
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const i18n = {
  en: {
    back: 'All Services',
    tagline: 'Social Media Management',
    title: 'Instagram Growth for Local Businesses',
    heroDesc: 'A consistent, on-brand Instagram presence that keeps your business top-of-mind, builds a real local community, and turns followers into walk-ins, calls and orders.',
    ctaStart: 'Apply Now',
    customPackage: 'Custom Packages Available',
    sectionWhatLabel: 'What We Do',
    sectionWhatTitle: 'Everything Your Instagram Needs',
    sectionWhatDesc: 'From content creation to community management — we run your Instagram so you can run your business.',
    f1Title: 'Instagram Posts & Carousels',
    f1Desc: 'High-quality feed posts that look intentional, branded and premium — designed to stop the scroll.',
    f2Title: 'Reels & Short-Form Video',
    f2Desc: 'Trending reels and short-form content shot on-site at your business to drive organic reach.',
    f3Title: 'Daily Stories',
    f3Desc: 'Behind-the-scenes, products, offers and updates — published daily to keep you top-of-mind.',
    f4Title: 'Account Management',
    f4Desc: 'Full Instagram profile management: bio, highlights, links, contact buttons and saved stories.',
    f5Title: 'Content Planning & Scheduling',
    f5Desc: 'Monthly content calendar planned around your business, your events and your offers.',
    f6Title: 'Captions & Hashtags',
    f6Desc: 'Local-focused captions and hashtag strategy targeting customers in your area, not random reach.',
    f7Title: 'Community Engagement',
    f7Desc: 'Replying to DMs, comments and reviews — building a real local community around your brand.',
    f8Title: 'Monthly Reporting',
    f8Desc: 'Clear monthly reports: followers, reach, engagement and what drove customers to your business.',
    sectionHowLabel: 'How It Works',
    sectionHowTitle: 'A Simple 4-Step Process',
    step1Title: 'On-Site Shoot',
    step1Desc: 'We visit your business to capture authentic photo & video content — products, space, team, atmosphere.',
    step2Title: 'Content Planning',
    step2Desc: 'We build a monthly content plan around your goals: posts, reels, stories and key dates.',
    step3Title: 'Publishing & Stories',
    step3Desc: 'We post consistently and run daily stories — you never have to think about Instagram again.',
    step4Title: 'Engage & Report',
    step4Desc: 'We reply to followers, manage DMs and send you a clear monthly report.',
    sectionWhyLabel: 'Why It Works',
    sectionWhyTitle: 'Built for Local, Not Influencers',
    w1Title: 'Real Local Reach',
    w1Desc: 'We target customers in your neighborhood — not vanity followers from across the world.',
    w2Title: 'Authentic Content',
    w2Desc: 'Real photos and videos shot at your business. No stock images, no AI graphics.',
    w3Title: 'Consistency',
    w3Desc: 'Daily activity keeps your business in the feed and in the conversation.',
    w4Title: 'Walk-Ins, Not Likes',
    w4Desc: 'Every post is built to drive real business — calls, visits, orders.',
    ctaTitle: 'Ready to Grow Your Instagram?',
    ctaDesc: "Let's build a content plan that actually brings customers through your door.",
    ctaButton: 'Apply Now'
  },
  tr: {
    back: 'Tüm Hizmetler',
    tagline: 'Sosyal Medya Yönetimi',
    title: 'Yerel İşletmeler İçin Instagram Büyümesi',
    heroDesc: 'İşletmenizi sürekli akılda tutan, gerçek bir yerel topluluk kuran ve takipçileri müşteriye, çağrıya ve siparişe dönüştüren tutarlı, markaya uygun bir Instagram varlığı.',
    ctaStart: 'Hemen Başvur',
    customPackage: 'Özel Paketler Mevcut',
    sectionWhatLabel: 'Neler Yapıyoruz',
    sectionWhatTitle: "Instagram'ınız İçin Gereken Her Şey",
    sectionWhatDesc: 'İçerik üretiminden topluluk yönetimine — biz Instagram\'ınızı yönetiyoruz, siz işinize odaklanıyorsunuz.',
    f1Title: 'Instagram Paylaşımları & Carousel',
    f1Desc: 'Markaya uygun, premium görünümlü yüksek kaliteli feed paylaşımları — kaydırmayı durduran tasarım.',
    f2Title: 'Reels & Kısa Format Video',
    f2Desc: 'Organik erişimi artırmak için işletmenizde çekilmiş trend Reels ve kısa format içerikler.',
    f3Title: 'Günlük Hikayeler',
    f3Desc: 'Sahne arkası, ürünler, teklifler ve güncellemeler — her gün paylaşılarak işletmeniz akılda kalır.',
    f4Title: 'Hesap Yönetimi',
    f4Desc: 'Tam Instagram profil yönetimi: biyografi, öne çıkanlar, linkler, iletişim butonları ve story arşivi.',
    f5Title: 'İçerik Planlama & Programlama',
    f5Desc: 'İşletmeniz, etkinlikleriniz ve kampanyalarınız etrafında planlanan aylık içerik takvimi.',
    f6Title: 'Açıklamalar & Hashtagler',
    f6Desc: 'Bölgenizdeki müşterileri hedefleyen yerel odaklı açıklamalar ve hashtag stratejisi.',
    f7Title: 'Topluluk Etkileşimi',
    f7Desc: 'DM\'lere, yorumlara ve yorumlara yanıt verme — markanız etrafında gerçek bir yerel topluluk kurma.',
    f8Title: 'Aylık Raporlama',
    f8Desc: 'Net aylık raporlar: takipçi, erişim, etkileşim ve müşteri getiren içerikler.',
    sectionHowLabel: 'Nasıl Çalışıyoruz',
    sectionHowTitle: 'Basit 4 Adımlı Süreç',
    step1Title: 'Yerinde Çekim',
    step1Desc: 'Özgün fotoğraf & video içeriği çekmek için işletmenize geliyoruz — ürünler, mekan, ekip, atmosfer.',
    step2Title: 'İçerik Planlama',
    step2Desc: 'Hedeflerinize göre aylık içerik planı kuruyoruz: paylaşımlar, reels, story\'ler ve önemli tarihler.',
    step3Title: 'Yayın & Story',
    step3Desc: 'Tutarlı paylaşıyor ve günlük story\'ler yayınlıyoruz — Instagram\'ı bir daha düşünmeniz gerekmez.',
    step4Title: 'Etkileşim & Rapor',
    step4Desc: 'Takipçilere yanıt veriyor, DM\'leri yönetiyor ve net bir aylık rapor gönderiyoruz.',
    sectionWhyLabel: 'Neden Çalışıyor',
    sectionWhyTitle: 'Influencer İçin Değil, Yerel İçin',
    w1Title: 'Gerçek Yerel Erişim',
    w1Desc: 'Mahalledeki müşterileri hedefliyoruz — dünyanın öbür ucundan boş takipçileri değil.',
    w2Title: 'Özgün İçerik',
    w2Desc: 'İşletmenizde çekilmiş gerçek fotoğraf ve videolar. Stok görsel, yapay zeka grafiği yok.',
    w3Title: 'Tutarlılık',
    w3Desc: 'Günlük etkinlik, işletmenizi her zaman akışta ve sohbette tutar.',
    w4Title: 'Beğeni Değil, Müşteri',
    w4Desc: 'Her paylaşım gerçek iş getirmek için tasarlandı — çağrı, ziyaret, sipariş.',
    ctaTitle: 'Instagram\'ınızı Büyütmeye Hazır mısınız?',
    ctaDesc: 'Gerçekten kapınızdan müşteri getirecek bir içerik planı kuralım.',
    ctaButton: 'Hemen Başvur'
  }
};

const SocialMediaManagementPage = () => {
  const { language } = useLanguage();
  const t = i18n[language] || i18n.en;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const features = [
    { icon: Instagram, title: t.f1Title, desc: t.f1Desc, color: '#EC4899' },
    { icon: Camera, title: t.f2Title, desc: t.f2Desc, color: '#F59E0B' },
    { icon: Eye, title: t.f3Title, desc: t.f3Desc, color: '#8B5CF6' },
    { icon: Users, title: t.f4Title, desc: t.f4Desc, color: '#38BDF8' },
    { icon: Calendar, title: t.f5Title, desc: t.f5Desc, color: '#10B981' },
    { icon: Hash, title: t.f6Title, desc: t.f6Desc, color: '#F472B6' },
    { icon: MessageCircle, title: t.f7Title, desc: t.f7Desc, color: '#06B6D4' },
    { icon: BarChart3, title: t.f8Title, desc: t.f8Desc, color: '#A78BFA' }
  ];

  const steps = [
    { num: '01', icon: Camera, title: t.step1Title, desc: t.step1Desc },
    { num: '02', icon: Calendar, title: t.step2Title, desc: t.step2Desc },
    { num: '03', icon: Zap, title: t.step3Title, desc: t.step3Desc },
    { num: '04', icon: BarChart3, title: t.step4Title, desc: t.step4Desc }
  ];

  const whyItems = [
    { icon: Sparkles, title: t.w1Title, desc: t.w1Desc },
    { icon: Camera, title: t.w2Title, desc: t.w2Desc },
    { icon: Calendar, title: t.w3Title, desc: t.w3Desc },
    { icon: Heart, title: t.w4Title, desc: t.w4Desc }
  ];

  return (
    <div className="min-h-screen bg-transparent relative z-10 pt-24" data-testid="social-media-page">
      {/* Hero */}
      <section className="py-16 lg:py-24 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute top-0 left-1/2 -translate-x-1/2 w-[1200px] h-[700px]"
            style={{ background: 'radial-gradient(ellipse at center top, rgba(236, 72, 153, 0.10) 0%, transparent 60%)' }}
          />
        </div>

        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative z-10">
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-white/40 hover:text-white text-sm mb-10 transition-colors"
            data-testid="back-to-services"
          >
            <ArrowLeft size={16} />
            <span>{t.back}</span>
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-4xl"
          >
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-pink-500/10 border border-pink-500/20 mb-8">
              <Instagram size={16} className="text-pink-400" />
              <span className="text-sm font-medium text-pink-400">{t.tagline}</span>
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-semibold text-white mb-6 leading-[1.05] tracking-tight">
              {t.title}
            </h1>
            <p className="text-xl text-white/55 leading-relaxed max-w-2xl mb-10">
              {t.heroDesc}
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/contact"
                className="group inline-flex items-center justify-center gap-3 h-14 px-8 bg-pink-500 text-white font-semibold rounded-full hover:bg-pink-400 transition-colors"
                data-testid="social-media-cta"
              >
                <span>{t.ctaStart}</span>
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What We Do — 8 features */}
      <section className="py-16 lg:py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-14 max-w-2xl"
          >
            <span className="text-[11px] font-semibold uppercase tracking-[0.25em] text-pink-400/70 block mb-3">
              {t.sectionWhatLabel}
            </span>
            <h2 className="text-3xl md:text-5xl font-semibold text-white mb-4 tracking-tight">
              {t.sectionWhatTitle}
            </h2>
            <p className="text-white/50 text-lg leading-relaxed">{t.sectionWhatDesc}</p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {features.map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="group relative p-6 rounded-2xl bg-[#0A0A0A]/70 border border-white/[0.06] hover:border-white/15 transition-all duration-300"
                data-testid={`sm-feature-${i}`}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-5 transition-transform group-hover:scale-110"
                  style={{ background: `${feature.color}15`, border: `1px solid ${feature.color}30` }}
                >
                  <feature.icon size={20} style={{ color: feature.color }} />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2 leading-tight">{feature.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works — 4 steps */}
      <section className="py-16 lg:py-24 border-t border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-14 max-w-2xl"
          >
            <span className="text-[11px] font-semibold uppercase tracking-[0.25em] text-pink-400/70 block mb-3">
              {t.sectionHowLabel}
            </span>
            <h2 className="text-3xl md:text-5xl font-semibold text-white tracking-tight">
              {t.sectionHowTitle}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {steps.map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="relative p-7 rounded-2xl bg-gradient-to-br from-pink-500/[0.04] to-transparent border border-white/[0.08] hover:border-pink-500/30 transition-all"
              >
                <div className="text-5xl font-bold text-pink-500/15 mb-4 leading-none">{step.num}</div>
                <step.icon size={22} className="text-pink-400 mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">{step.title}</h3>
                <p className="text-white/55 text-sm leading-relaxed">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why It Works */}
      <section className="py-16 lg:py-24 border-t border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-14 max-w-2xl"
          >
            <span className="text-[11px] font-semibold uppercase tracking-[0.25em] text-pink-400/70 block mb-3">
              {t.sectionWhyLabel}
            </span>
            <h2 className="text-3xl md:text-5xl font-semibold text-white tracking-tight">
              {t.sectionWhyTitle}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {whyItems.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="p-6 rounded-2xl bg-[#0A0A0A]/60 border border-white/[0.06]"
              >
                <div className="w-12 h-12 rounded-xl bg-pink-500/10 border border-pink-500/20 flex items-center justify-center mb-4">
                  <item.icon size={20} className="text-pink-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative p-12 lg:p-16 rounded-3xl bg-gradient-to-br from-pink-500/10 to-transparent border border-pink-500/20 text-center overflow-hidden"
          >
            <div
              className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] pointer-events-none"
              style={{ background: 'radial-gradient(ellipse at center, rgba(236, 72, 153, 0.18) 0%, transparent 70%)' }}
            />
            <div className="relative z-10">
              <h2 className="text-3xl md:text-5xl font-semibold text-white mb-4">{t.ctaTitle}</h2>
              <p className="text-white/55 text-lg mb-8 max-w-xl mx-auto">{t.ctaDesc}</p>
              <Link
                to="/contact"
                className="group inline-flex items-center gap-3 h-14 px-8 bg-pink-500 text-white font-semibold rounded-full hover:bg-pink-400 transition-colors"
                data-testid="social-media-cta-bottom"
              >
                <span>{t.ctaButton}</span>
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default SocialMediaManagementPage;
